import { DataTable, DataTableSearchConfig } from '@/components/DataTable';
import { useEffect, useMemo, useRef, useState } from 'react';
import { OneLineText } from '@/components/OneLineText';
import { ColumnDef, Row } from '@tanstack/react-table';
import { FormProvider, useForm } from 'react-hook-form';
import { HOME_INSPECTION_RUSH_FILTER_OPTIONS, HOME_INSPECTION_STATUS_OPTIONS } from '@/utils/constant';
import { useFilterStore } from '@/stores/filterStore';
import { AdvanceFilterFieldType, AdvanceFilterOperator } from '@/types/types';
import { useTeamGetAll } from '@/services/teamService';
import { useUserStore } from '@/stores/user';
import { hasFeature } from '@/services/featureService';
import { Feature } from '@/utils/constant';
import { convertISODateToMMDDYYYY, displayFullName, downloadFilefromBuffer } from '@/lib/utils';
import { HIAdvanceFilters } from './HIAdvanceFilters';
import {
  hiFiltersToParams,
  useHIWorkOrders,
  exportHIORPdf,
  useUpdateHIWorkOrderStatus,
  useAssignHomeInspectionWorkOrder,
} from '@/services/homeInspectionService';
import { toast } from '@/components/ui/use-toast';
import { displayError } from '@/lib/error';
import type { GenericErrorResponse } from '@brainvaulttech/dgr-react-sdk';
import type {
  AssignTeamUserRequest,
  HomeInspectionWorkOrderResponse,
  HomeInspectionWorkOrderStatus,
} from '@/types/homeInspection.types';
import { useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import type { HIWorkOrderListParams, HIWorkOrderListResponse } from '@/types/homeInspection.types';
import { FormFieldSelect } from '@/components/FormFieldSelect';
import axios from 'axios';
import { ServiceSwitcher } from '@/components/ServiceSwitcher';
import { userGetAll } from '@brainvaulttech/dgr-react-sdk';
import { useNavigate, useLocation } from 'react-router-dom';
import { Pencil, Download, MessageSquare, Flag, Ellipsis } from 'lucide-react';
import HIWorkOrderSidePanel from './HIWorkOrderSidePanel';
import HICommentsDialog from './HICommentsDialog';
import { canAccessHomeInspectionWorkOrders, isHomeInspectionServiceTeamMember } from './hiPermissions';
import { isAdminOrSupervisor } from '@/services/userService';
import type { Team } from '@/types/team.types';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

type HIRowActionsProps = {
  row: Row<HomeInspectionWorkOrderResponse>;
  navigate: ReturnType<typeof useNavigate>;
  locationPathname: string;
  onOpenComments: (workOrderId: string) => void;
  commentCount: number;
};

const canUserAssignToSelf = (workOrderTeamId: string | null, userTeams: Team[]) => {
  return userTeams.some((team) => team.id === workOrderTeamId);
};

export const HIRowActions = ({ row, navigate, locationPathname, onOpenComments, commentCount }: HIRowActionsProps) => {
  const [isExporting, setIsExporting] = useState(false);
  const queryClient = useQueryClient();
  const loggedInUser = useUserStore((state) => state.user);
  const assignMutation = useAssignHomeInspectionWorkOrder();
  const { data: teamsData } = useTeamGetAll();

  const isAdminOrSup = isAdminOrSupervisor(loggedInUser);
  const isServiceMember = isHomeInspectionServiceTeamMember(loggedInUser);
  // API assign-team-user is gated by homeInspection.workOrder.create
  const canAssignAtAll =
    hasFeature(Feature.HOME_INSPECTION) &&
    hasFeature(Feature.HOME_INSPECTION_WORK_ORDER_CREATE) &&
    canAccessHomeInspectionWorkOrders(loggedInUser) &&
    (isAdminOrSup || isServiceMember);
  const canAssignTeamOrUser = canAssignAtAll && isAdminOrSup;
  const isRowAssignable = row.original.status !== 'CANCELED';
  // Non-admins: Assign Me only when on the work order's team (matches API checkAssignTeamUserPermission).
  // Admins/supervisors may Assign Me on any row.
  const canShowAssignMe =
    canAssignAtAll &&
    isRowAssignable &&
    !!loggedInUser?.userId &&
    row.original.userId !== loggedInUser.userId &&
    (isAdminOrSup || canUserAssignToSelf(row.original.teamId ?? null, loggedInUser?.teams ?? []));

  const showAssignMenu = canAssignAtAll && (canShowAssignMe || (canAssignTeamOrUser && isRowAssignable));
  const teamsOptions = useMemo(
    () =>
      teamsData?.teams
        .filter((team) => !['ADMIN', 'PROCESS_SERVER'].includes(team.key))
        .map((team) => ({ label: team.name, value: team.id, team })) ?? [],
    [teamsData]
  );

  const userOptions = useMemo(() => {
    const sourceTeams = row.original.teamId
      ? teamsOptions.filter((t) => t.value === row.original.teamId)
      : teamsOptions;
    const seen = new Set<string>();
    const options: { label: string; value: string }[] = [];
    for (const t of sourceTeams) {
      for (const user of t.team.users ?? []) {
        if (seen.has(user.userId)) continue;
        seen.add(user.userId);
        options.push({
          label: `${user.firstName} ${user.lastName}`.trim(),
          value: user.userId,
        });
      }
    }
    return options;
  }, [teamsOptions, row.original.teamId]);

  const handleAssign = (body: AssignTeamUserRequest, successMessage: string) => {
    if (assignMutation.isPending) return;
    assignMutation.mutate(
      { id: row.original.workOrderId, body },
      {
        onSuccess: (updated) => {
          Object.assign(row.original, updated);
          void queryClient.invalidateQueries({ queryKey: ['hi-work-orders'] });
          void queryClient.invalidateQueries({ queryKey: ['hi-work-order-by-number'] });
          toast({
            variant: 'default',
            description: successMessage,
            duration: 3000,
          });
        },
        onError: (error) => {
          if (axios.isAxiosError(error) && error.response?.data) {
            displayError(error.response.data, 5000);
          } else {
            toast({
              variant: 'destructive',
              description: 'Failed to update work order assignment.',
              duration: 5000,
            });
          }
        },
      }
    );
  };

  return (
    <div className="flex items-center gap-4 me-5" onClick={(e) => e.stopPropagation()}>
      {hasFeature(Feature.HOME_INSPECTION_WORK_ORDER_UPDATE) && row.original.status !== 'CANCELED' && (
        <div
          role="button"
          title="Edit Work Order"
          className="cursor-pointer text-primary"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`${locationPathname}/${row.original.workOrderId}/update`);
          }}
        >
          <Pencil className="h-5 w-5" />
        </div>
      )}
      {hasFeature(Feature.HOME_INSPECTION_EXPORT_HIOR_PDF) && !!row.original.server && (
        <div
          role="button"
          title="Export HIOR"
          className={`text-primary ${isExporting ? 'opacity-50 cursor-not-allowed pointer-events-none' : 'cursor-pointer'}`}
          onClick={async (e) => {
            e.stopPropagation();
            if (isExporting) return;
            setIsExporting(true);
            try {
              const { data, error } = await exportHIORPdf(row.original.workOrderId);
              if (error) {
                toast({ description: 'FAILED TO EXPORT HIOR PDF', variant: 'destructive', duration: 5000 });
              } else if (data) {
                downloadFilefromBuffer(data, `${row.original.workOrderNumber}.pdf`, 'PDF');
              }
            } finally {
              setIsExporting(false);
            }
          }}
        >
          <Download className="h-5 w-5" />
        </div>
      )}
      {hasFeature(Feature.HOME_INSPECTION_WORK_ORDER_COMMENTS) && (
        <button
          type="button"
          title="View Comments"
          className="relative cursor-pointer text-primary"
          onClick={(e) => {
            e.stopPropagation();
            onOpenComments(row.original.workOrderId);
          }}
          onKeyDown={(e) => {
            e.stopPropagation();
          }}
        >
          <MessageSquare className="h-5 w-5" />
          {commentCount > 0 && (
            <span className="absolute -top-2 -right-2 flex items-center justify-center min-w-[16px] h-[16px] px-1 text-[8px] font-semibold text-white bg-primary rounded-full">
              {commentCount}
            </span>
          )}
        </button>
      )}
      {showAssignMenu && (
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <div role="button" title="More" className="cursor-pointer text-primary">
              <Ellipsis className="h-5 w-5" />
            </div>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="bg-white border-[1px] border-bordercolor z-[1000] rounded-md max-h-[300px] overflow-auto custom-scrollbar">
            {canShowAssignMe && (
              <>
                <DropdownMenuCheckboxItem
                  className="!opacity-100 !font-semibold"
                  disabled={assignMutation.isPending}
                  onClick={() => {
                    if (!loggedInUser?.userId) return;
                    handleAssign(
                      { userId: loggedInUser.userId },
                      'Work order assigned to you successfully.'
                    );
                  }}
                >
                  Assign Me
                </DropdownMenuCheckboxItem>
                {(canAssignTeamOrUser && isRowAssignable) && <hr />}
              </>
            )}
            {canAssignTeamOrUser && isRowAssignable && (
              <>
                <DropdownMenuCheckboxItem disabled className="!opacity-100 !font-semibold">
                  Assign Team
                </DropdownMenuCheckboxItem>
                <hr />
                {teamsOptions.map((team) => (
                  <DropdownMenuCheckboxItem
                    key={team.value}
                    checked={row.original.teamId === team.value}
                    disabled={assignMutation.isPending}
                    onClick={() => {
                      // Re-selecting the current team re-calls assign and nulls userId on the API
                      if (row.original.teamId === team.value) return;
                      handleAssign({ teamId: team.value }, 'Work order team updated successfully.');
                    }}
                  >
                    <div className="flex flex-row justify-start items-center gap-2 w-full font-[500] text-3.5">
                      <span>{team.label}</span>
                    </div>
                  </DropdownMenuCheckboxItem>
                ))}
                <hr />
                <DropdownMenuCheckboxItem disabled className="!opacity-100 !font-semibold">
                  Assign User
                </DropdownMenuCheckboxItem>
                <hr />
                {userOptions.map((user) => (
                  <DropdownMenuCheckboxItem
                    key={user.value}
                    checked={row.original.userId === user.value}
                    disabled={assignMutation.isPending}
                    onClick={() => {
                      if (row.original.userId === user.value) return;
                      handleAssign({ userId: user.value }, 'Work order user updated successfully.');
                    }}
                  >
                    <div className="flex flex-row justify-start items-center gap-2 w-full font-[500] text-3.5">
                      <span>{user.label}</span>
                    </div>
                  </DropdownMenuCheckboxItem>
                ))}
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      )}    </div>
  );
};

const statusClassMap: Record<string, string> = {
  CANCELED: 'bg-[#F5F5F5] text-[#676767] hover:bg-[#F5F5F5]',
  ASSIGNED: 'bg-[#EDF5FF] font-medium text-primary hover:bg-[#EDF5FF]',
  AWAITING_CLIENT: 'bg-[#FFEEBF] text-[#6C4A09] hover:bg-[#FFEEBF]',
};

const ONE_YEAR_MS = 365 * 24 * 60 * 60 * 1000;
const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;

const canModifyCanceledStatus = (
  workOrder: HomeInspectionWorkOrderResponse,
  user: Parameters<typeof isAdminOrSupervisor>[0]
): boolean => {
  if (workOrder.status !== 'CANCELED') return true;
  // TODO: replace updatedAt with canceledAt once field is available
  const elapsed = Date.now() - new Date(workOrder.updatedAt).getTime();
  if (isAdminOrSupervisor(user)) {
    return elapsed < ONE_YEAR_MS;
  }
  return elapsed < TWENTY_FOUR_HOURS_MS;
};

const HIList = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const filterStore = useFilterStore((state) => state);
  const { hiFilters, setHIFilters } = filterStore;
  const skipDefaultFilterRef = useRef(false);

  const methods = useForm();
  const loggedInUser = useUserStore((state) => state.user);
  const userTeams = loggedInUser?.teams ?? [];

  const statusUpdateMutation = useUpdateHIWorkOrderStatus();
  const queryClient = useQueryClient();

  const canUpdateStatus =
    hasFeature(Feature.HOME_INSPECTION_EDIT_STATUS) &&
    (isAdminOrSupervisor(loggedInUser) || userTeams.some((t) => t.key === 'SERVICE'));

  const canCreate =
    hasFeature(Feature.HOME_INSPECTION_WORK_ORDER_CREATE) && canAccessHomeInspectionWorkOrders(loggedInUser);

  const canViewPanel = hasFeature(Feature.HOME_INSPECTION) && canAccessHomeInspectionWorkOrders(loggedInUser);

  const [selectedWorkOrderNumber, setSelectedWorkOrderNumber] = useState<string | null>(null);
  const [commentWorkOrderId, setCommentWorkOrderId] = useState<string | null>(null);
  const [openCommentPanel, setOpenCommentPanel] = useState(false);
  const [commentCountMap, setCommentCountMap] = useState<Record<string, number>>({});

  const handleOpenComments = (workOrderId: string) => {
    setCommentWorkOrderId(workOrderId);
    setOpenCommentPanel(true);
  };

  const handleCloseComments = () => {
    setOpenCommentPanel(false);
    setCommentWorkOrderId(null);
  };

  const handleCountUpdate = (workOrderId: string, count: number) => {
    setCommentCountMap((prev) => ({ ...prev, [workOrderId]: count }));
  };

  const isServer = loggedInUser?.roleId === 'SERVER';
  const isSingleTeam = !isServer && userTeams.length <= 1;
  const lockTeam = isServer || isSingleTeam;

  const [isAdvanceFilterOpen, setIsAdvanceFilterOpen] = useState(false);

  const { data: teams } = useTeamGetAll();

  const teamsOptions = useMemo(() => teams?.teams.map((t) => ({ label: t.name, value: t.id })) ?? [], [teams]);

  const { data: allServersData } = useQuery({
    queryKey: ['servers-active'],
    queryFn: () => userGetAll({ roleId: 'SERVER', pageSize: 1000 }),
    staleTime: 5 * 60 * 1000,
  });

  const serverOptions = useMemo(
    () =>
      (
        (allServersData?.data as { users?: { userId: string; firstName: string; lastName: string; status: string }[] })
          ?.users ?? []
      )
        .filter((u) => u.status === 'ACTIVE')
        .map((u) => ({
          label: `${u.firstName} ${u.lastName}`.trim(),
          value: u.userId,
        })),
    [allServersData]
  );

  const baseSearchFields: DataTableSearchConfig[] = useMemo(
    () => [
      { label: 'Search Work Order Number', name: 'workOrderNumber', placeholder: 'Search Work Order Number' },
      { label: 'Search Client ID', name: 'clientId', placeholder: 'Search Client Code' },
      { label: 'Search Case Number', name: 'caseNumber', placeholder: 'Search Case Number' },
      { label: 'Server', name: 'serverId', placeholder: 'Select Server', fieldType: 'SELECT', options: serverOptions },
      {
        label: 'Status',
        name: 'status',
        placeholder: 'Select Status',
        fieldType: 'SELECT',
        options: HOME_INSPECTION_STATUS_OPTIONS,
      },
      {
        label: 'Team',
        name: 'teamId',
        placeholder: 'Select Team',
        fieldType: 'SELECT',
        options: teamsOptions,
        stickyItem: { label: 'My Teams', value: 'MY_TEAMS' },
      },
    ],
    [teamsOptions]
  );

  const hiSearchFields = useMemo(
    () => (lockTeam ? baseSearchFields.filter((f) => f.name !== 'teamId') : baseSearchFields),
    [baseSearchFields, lockTeam]
  );

  useEffect(() => {
    if (hiFilters.length > 0 || skipDefaultFilterRef.current) return;

    const defaultTeamValue = lockTeam ? (userTeams[0]?.id ?? 'MY_TEAMS') : 'MY_TEAMS';
    setHIFilters([
      {
        field: 'teamId',
        operator: AdvanceFilterOperator.EQUALS,
        value: defaultTeamValue,
        fieldType: AdvanceFilterFieldType.ID,
      },
    ]);
  }, [hiFilters.length, lockTeam, setHIFilters, userTeams]);

  const getHIWorkOrdersData = (
    params: HIWorkOrderListParams & { filters?: typeof hiFilters },
    options?: { query?: UseQueryOptions<HIWorkOrderListResponse> }
  ) => {
    const { filters, pageNo, pageSize, rushType } = params;
    const apiParams = {
      ...hiFiltersToParams(filters ?? []),
      pageNo,
      pageSize,
      rushType: rushType ?? undefined,
    };
    const query = useHIWorkOrders(apiParams, options);
    const data = query.data?.workOrders ?? [];
    const totalRows = query.data?.pagination?.total ?? data.length;
    return { ...query, data, totalRows };
  };

  const columns: ColumnDef<HomeInspectionWorkOrderResponse>[] = [
    {
      accessorKey: 'workOrderNumber',
      header: 'W/O No.',
      enableSorting: true,
      size: 120,
      cell: ({ row }) => {
        const isRush = row.original.rushType !== 'NONE';
        return (
          <div className={`${isRush ? 'ml-[-10px]' : 'pl-1'} flex flex-row gap-1 items-center min-w-[100px] mr-5`}>
            {isRush && <Flag className="text-[#D72222] w-3 h-3 shrink-0" fill="#D72222" />}
            <OneLineText text={row.original.workOrderNumber} />
          </div>
        );
      },
    },
    {
      accessorKey: 'clientName',
      header: 'Client',
      enableSorting: true,
      cell: ({ row }) => <OneLineText text={row.original.clientName} className="min-w-[100px] mr-5" />,
    },
    {
      accessorKey: 'caseNumber',
      header: 'Case #',
      cell: ({ row }) => <OneLineText text={row.original.caseNumber ?? '-'} className="min-w-[80px] mr-5" />,
    },
    {
      accessorKey: 'propertyAddress',
      header: 'Property Address',
      cell: ({ row }) => <OneLineText text={row.original.propertyAddress} className="min-w-[150px] mr-5" />,
    },
    {
      accessorKey: 'city',
      header: 'City',
      cell: ({ row }) => <OneLineText text={row.original.city} className="min-w-[80px] mr-5" />,
    },
    {
      accessorKey: 'server',
      header: 'Server',
      cell: ({ row }) => (
        <OneLineText className="min-w-[120px] mr-5" text={displayFullName(row.original.server ?? undefined) ?? ''} />
      ),
    },
    {
      accessorKey: 'team',
      header: 'Team',
      cell: ({ row }) => <OneLineText text={row.original.team?.name ?? '-'} className="min-w-[80px] mr-5" />,
    },
    {
      accessorKey: 'status',
      header: 'Status',
      size: 160,
      cell: ({ row }) => {
        const previousStatus = row.original.status;
        const canEdit = canUpdateStatus && canModifyCanceledStatus(row.original, loggedInUser);

        if (!canEdit) {
          return (
            <div
              className={`h-[20px] w-[140px] text-xs font-medium mr-5 rounded-full flex items-center justify-center ${statusClassMap[previousStatus] ?? ''}`}
            >
              {HOME_INSPECTION_STATUS_OPTIONS.find((o) => o.value === previousStatus)?.label ?? previousStatus}
            </div>
          );
        }

        return (
          <div onClick={(e) => e.stopPropagation()}>
            <FormFieldSelect
              name={`hi-status-${row.original.workOrderId}`}
              hasControl={false}
              isLoading={
                statusUpdateMutation.status === 'pending' &&
                row.original.workOrderId === statusUpdateMutation.variables?.id
              }
              options={HOME_INSPECTION_STATUS_OPTIONS}
              className={`h-[20px] w-[140px] text-xs font-medium mr-5 rounded-full ${statusClassMap[row.original.status] ?? ''}`}
              defaultValue={row.original.status}
              onChange={(value) => {
                if (!value) return;
                statusUpdateMutation.mutate(
                  { id: row.original.workOrderId, status: value as HomeInspectionWorkOrderStatus },
                  {
                    onSuccess: async (data) => {
                      queryClient.setQueriesData<HIWorkOrderListResponse>(
                        { queryKey: ['hi-work-orders'] },
                        (old) => {
                          if (!old) return old;
                          return {
                            ...old,
                            workOrders: old.workOrders.map((wo) =>
                              wo.workOrderId === row.original.workOrderId
                                ? { ...wo, status: data.status, updatedAt: data.updatedAt }
                                : wo
                            ),
                          };
                        }
                      );
                      await queryClient.invalidateQueries({ queryKey: ['hi-work-orders'] });
                    },
                    onError: (error) => {
                      queryClient.setQueriesData<HIWorkOrderListResponse>(
                        { queryKey: ['hi-work-orders'] },
                        (old) => {
                          if (!old) return old;
                          return {
                            ...old,
                            workOrders: old.workOrders.map((wo) =>
                              wo.workOrderId === row.original.workOrderId
                                ? { ...wo, status: previousStatus }
                                : wo
                            ),
                          };
                        }
                      );
                      if (axios.isAxiosError(error) && error.response?.data) {
                        displayError(error.response.data as GenericErrorResponse, 5000);
                      } else {
                        toast({
                          variant: 'destructive',
                          description: 'Failed to update work order status.',
                          duration: 5000,
                        });
                      }
                    },
                  }
                );
              }}
            />
          </div>
        );
      },
    },
    {
      accessorKey: 'createdAt',
      header: 'Entry Date',
      enableSorting: true,
      cell: ({ row }) => (
        <OneLineText text={convertISODateToMMDDYYYY(row.original.createdAt)} className="min-w-[100px] mr-5" />
      ),
    },
    {
      id: 'actions',
      header: '',
      size: 120,
      cell: ({ row }) => (
        <HIRowActions
          row={row}
          navigate={navigate}
          locationPathname={location.pathname}
          onOpenComments={handleOpenComments}
          commentCount={commentCountMap[row.original.workOrderId] ?? row.original.commentsCount ?? 0}
        />
      ),
    },
  ];

  if (!hasFeature(Feature.HOME_INSPECTION)) return null;

  return (
    <div className="flex w-[100%]">
      <FormProvider {...methods}>
        <DataTable
          columns={columns}
          apiGet={getHIWorkOrdersData}
          onRowClick={(row, e) => {
            const target = e.target as HTMLElement;
            if (target.closest('.action')) return;
            if (canViewPanel) setSelectedWorkOrderNumber(row.workOrderNumber);
          }}
          toolbar={{
            toolBarTitle: 'Home Inspection Maintenance',
            addButton: canCreate,
            addButtonTitle: 'Add Entry',
            addOnClick: () => navigate('/home-inspection/create'),
            serviceSwitcher: <ServiceSwitcher currentService="home-inspection" />,
            filters: {
              groups: [
                {
                  name: 'rushType',
                  label: 'Rush',
                  options: HOME_INSPECTION_RUSH_FILTER_OPTIONS,
                  defaultValue: null,
                },
              ],
            },
          }}
          search={hiSearchFields}
          onSearch={(search) => {
            if (!search) {
              skipDefaultFilterRef.current = true;
              setHIFilters([]);
              return;
            }

            const updatedFilters = [...hiFilters];

            Object.entries(search).forEach(([field, value]) => {
              const strValue = value instanceof Date ? value.toISOString() : (value as string);
              const existingIndex = updatedFilters.findIndex((f) => f.field === field);

              if (!strValue) {
                if (existingIndex !== -1) updatedFilters.splice(existingIndex, 1);
              } else {
                const isContainsField = field === 'caseNumber';
                const entry = {
                  field,
                  value: strValue,
                  operator: isContainsField ? AdvanceFilterOperator.CONTAINS : AdvanceFilterOperator.EQUALS,
                  fieldType: isContainsField ? AdvanceFilterFieldType.STRING : AdvanceFilterFieldType.ID,
                };
                if (existingIndex !== -1) {
                  updatedFilters[existingIndex] = entry;
                } else {
                  updatedFilters.push(entry);
                }
              }
            });

            skipDefaultFilterRef.current = updatedFilters.length === 0;
            setHIFilters(updatedFilters);
          }}
          advanceFilter={{
            filters: hiFilters,
            setIsAdvanceFilterOpen: setIsAdvanceFilterOpen,
          }}
        />
      </FormProvider>
      {selectedWorkOrderNumber && (
        <HIWorkOrderSidePanel workOrderNumber={selectedWorkOrderNumber} onClose={() => setSelectedWorkOrderNumber(null)} />
      )}
      {openCommentPanel && commentWorkOrderId && (
        <HICommentsDialog
          workOrderId={commentWorkOrderId}
          open={openCommentPanel}
          onClose={handleCloseComments}
          onCountUpdate={handleCountUpdate}
        />
      )}
      <HIAdvanceFilters
        open={isAdvanceFilterOpen}
        onClose={() => setIsAdvanceFilterOpen(false)}
        filters={hiFilters}
        setFilters={setHIFilters}
        hiListViewFilters={hiSearchFields.map((f) => f.name)}
      />
    </div>
  );
};

export default HIList;
